import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Politique de Confidentialité
            </h1>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-sm text-gray-500 mb-8">
              Dernière mise à jour : 8 décembre 2025
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Lyon Picks ("nous", "notre", "nos") s'engage à protéger votre vie privée et vos données personnelles. Cette politique de 
                confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations personnelles lorsque 
                vous utilisez notre site web lyonpicks.com.
              </p>
              <p>
                En utilisant notre site, vous acceptez les pratiques décrites dans cette politique. Si vous n'acceptez pas cette politique, 
                veuillez ne pas utiliser notre site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-purple-600" />
                2. Données que Nous Collectons
              </h2>
              <p>
                Nous collectons différents types de données selon votre utilisation de notre site :
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">2.1. Données que Vous Nous Fournissez</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone (si fourni via le formulaire de contact)</li>
                <li>Messages et communications que vous nous envoyez</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">2.2. Données Collectées Automatiquement</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Adresse IP</li>
                <li>Type de navigateur et version</li>
                <li>Système d'exploitation</li>
                <li>Pages visitées et temps passé sur chaque page</li>
                <li>Référent (site web d'origine)</li>
                <li>Données de cookies et technologies similaires</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Comment Nous Utilisons Vos Données</h2>
              <p>Nous utilisons vos données personnelles pour les finalités suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Fournir et améliorer nos services de comparaison et d'information</li>
                <li>Répondre à vos demandes et communications</li>
                <li>Vous envoyer des informations sur nos services (avec votre consentement)</li>
                <li>Analyser l'utilisation de notre site pour l'améliorer</li>
                <li>Respecter nos obligations légales</li>
                <li>Protéger nos droits et notre sécurité</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Partage de Vos Données</h2>
              <p>
                Nous ne vendons jamais vos données personnelles à des tiers. Nous pouvons partager vos données uniquement dans les cas suivants :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Prestataires de services</strong> : Nous pouvons partager des données avec des prestataires qui nous aident à 
                faire fonctionner notre site (hébergement, analyse, etc.), sous contrat strict de confidentialité.</li>
                <li><strong>Obligations légales</strong> : Si la loi l'exige ou si nous recevons une demande légale valide.</li>
                <li><strong>Protection de nos droits</strong> : Pour protéger nos droits, propriété ou sécurité, ou ceux de nos utilisateurs.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-purple-600" />
                5. Cookies et Technologies Similaires
              </h2>
              <p>
                Notre site utilise des cookies et technologies similaires pour améliorer votre expérience. Pour plus d'informations détaillées, 
                consultez notre <Link to="/cookies" data-discover="true" className="text-purple-600 hover:text-purple-700 underline">Politique des Cookies</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sécurité de Vos Données</h2>
              <p>
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles 
                contre l'accès non autorisé, la perte, la destruction ou l'altération. Cependant, aucune méthode de transmission sur Internet 
                n'est totalement sécurisée, et nous ne pouvons garantir une sécurité absolue.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Vos Droits</h2>
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Droit d'accès</strong> : Vous pouvez demander une copie de vos données personnelles que nous détenons.</li>
                <li><strong>Droit de rectification</strong> : Vous pouvez corriger vos données inexactes ou incomplètes.</li>
                <li><strong>Droit à l'effacement</strong> : Vous pouvez demander la suppression de vos données dans certaines circonstances.</li>
                <li><strong>Droit à la limitation</strong> : Vous pouvez demander la limitation du traitement de vos données.</li>
                <li><strong>Droit à la portabilité</strong> : Vous pouvez recevoir vos données dans un format structuré.</li>
                <li><strong>Droit d'opposition</strong> : Vous pouvez vous opposer au traitement de vos données pour certaines finalités.</li>
                <li><strong>Droit de retirer votre consentement</strong> : Si le traitement est basé sur votre consentement.</li>
              </ul>
              <p className="mt-4">
                Pour exercer ces droits, contactez-nous à <a href="mailto:info@lyonpicks.com" className="text-purple-600 hover:text-purple-700 underline">info@lyonpicks.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Conservation des Données</h2>
              <p>
                Nous conservons vos données personnelles uniquement aussi longtemps que nécessaire pour les finalités pour lesquelles elles 
                ont été collectées, ou conformément aux obligations légales. Les données de contact sont conservées jusqu'à ce que vous 
                demandiez leur suppression ou jusqu'à 3 ans d'inactivité.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Liens vers d'Autres Sites</h2>
              <p>
                Notre site contient des liens vers des sites de paris en ligne externes. Nous ne sommes pas responsables des pratiques de 
                confidentialité de ces sites tiers. Nous vous encourageons à lire les politiques de confidentialité de chaque site que vous 
                visitez.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications de Cette Politique</h2>
              <p>
                Nous pouvons modifier cette politique de confidentialité occasionnellement. Les modifications seront publiées sur cette page 
                avec une date de mise à jour révisée. Nous vous encourageons à consulter régulièrement cette page pour rester informé de 
                nos pratiques.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
              <p>
                Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, contactez-nous :
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mt-4">
                <p><strong>Email :</strong> <a href="mailto:info@lyonpicks.com" className="text-purple-600 hover:text-purple-700 underline">info@lyonpicks.com</a></p>
                <p><strong>Téléphone :</strong> <a href="tel:+33289858716" className="text-purple-600 hover:text-purple-700 underline">+33 2 89 85 87 16</a></p>
                <p><strong>Adresse :</strong> Paris, France</p>
              </div>
            </section>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

